import React, { useEffect, useState, memo } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, TextField } from '@mui/material';
import { COUNTRIES } from '../../config/const.config';
import {
  fastSelectStyles,
  fastLabelStyles,
  fastErrorStyles,
} from '../../theme/fast-render-styles';

const Countries = memo(
  ({
    id,
    name,
    label,
    defaultValue,
    multiple,
    disabled,
    onChange,
    sx,
    error,
    renderFast = false,
  }) => {
    const selectedValue = defaultValue || (multiple ? [] : null);

    const [value, setValue] = useState(null);
    const [inputValue, setInputValue] = useState('');

    useEffect(() => {
      setValue(
        typeof selectedValue === 'object'
          ? selectedValue
          : COUNTRIES.filter((item) => item.value === selectedValue)?.[0]
      );
    }, [selectedValue]);

    if (renderFast) {
      const handleSelectChange = (e) => {
        if (multiple) {
          const selectedOptions = Array.from(e.target.selectedOptions);
          const selectedValues = selectedOptions.map((option) => option.value);
          onChange(selectedValues.length > 0 ? selectedValues : []);
        } else {
          const tempSelectedValue = e.target.value || null;
          onChange(tempSelectedValue);
        }
      };

      const getSelectedValue = () => {
        if (multiple) {
          if (Array.isArray(selectedValue)) {
            return selectedValue;
          }
          if (typeof selectedValue === 'string') {
            return [selectedValue];
          }
          return [];
        }
        return selectedValue || '';
      };

      const selectStyles = {
        ...fastSelectStyles,
        ...(multiple ? { height: 'auto', minHeight: '120px' } : {}),
        ...(error ? { borderColor: '#d32f2f' } : {}),
        ...(disabled ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
        ...sx,
      };

      return (
        <div style={{ width: '100%' }}>
          {label && (
            <label htmlFor={id} style={fastLabelStyles}>
              {label}
            </label>
          )}
          <select
            id={id}
            name={name}
            multiple={multiple}
            value={getSelectedValue()}
            onChange={handleSelectChange}
            disabled={disabled}
            style={selectStyles}
          >
            {!multiple && <option value="">Select a country</option>}
            {COUNTRIES.map((country) => (
              <option key={country.value} value={country.value}>
                {country.label}
              </option>
            ))}
          </select>
          {error && <div style={fastErrorStyles}>{error}</div>}
        </div>
      );
    }

    return (
      <Autocomplete
        id={id}
        name={name}
        options={COUNTRIES}
        getOptionLabel={(option) => option.label}
        sx={sx}
        value={value}
        onChange={(e, v) => {
          setValue(v);
          onChange(v?.value);
        }}
        inputValue={inputValue}
        onInputChange={(e, v) => {
          setInputValue(v);
        }}
        multiple={multiple}
        disabled={disabled}
        isOptionEqualToValue={(op, vl) => op.value === vl.value}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            error={error !== ''}
            helperText={error}
            variant="filled"
          />
        )}
      />
    );
  }
);

Countries.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  defaultValue: PropTypes.any,
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  sx: PropTypes.shape(),
  error: PropTypes.string,
  renderFast: PropTypes.bool,
};
Countries.defaultProps = {
  multiple: false,
  disabled: false,
  sx: {},
  defaultValue: null,
  error: '',
  renderFast: false,
};

export default Countries;
